import { toBoolOrNull, toIntOrNull } from '@utils/parse';
import { useApiModel } from '@hooks/use-api-model';
import { useApiSubmit } from '@hooks/use-api-submit';
import { DashRegistrationStep } from '@/types/dashboard';
import type {
    IPendingSurgeryFormValues,
    IPendingSurgeryModel,
    IPendingSurgerySubmitResult,
} from '@/types/surveys/pending-surgery';

const PENDING_SURGERY_ENDPOINT = '/api/register/pending-surgery';

const defaultPendingSurgeryModel: IPendingSurgeryModel = {
    id: 0,
    editKey: '',
    hasBeenScheduled: false,
    scheduledDateTime: null,

    languageData: {},
};

export const usePendingSurgery = (registrationId: number, editKey: string) => {
    const url = `${PENDING_SURGERY_ENDPOINT}/${registrationId}/${editKey}`;

    const { model, isLoading, error } = useApiModel<IPendingSurgeryModel>(url, defaultPendingSurgeryModel);

    const { submit, isSubmitting, submitError } = useApiSubmit<IPendingSurgeryFormValues, DashRegistrationStep, IPendingSurgerySubmitResult>(
        url,
        (values) => {
            // The sub-questions only exist while their parent answer is Yes, so anything
            // behind a No is sent empty rather than as whatever was typed before.
            const hasBeenScheduled = toBoolOrNull(values.hasBeenScheduled);
            const hasCompleted = hasBeenScheduled === true ? toBoolOrNull(values.hasCompleted) : null;

            return {
                hasBeenScheduled,
                scheduledDateTime: hasBeenScheduled === true && values.scheduledDateTime !== '' ? values.scheduledDateTime : null,
                hasCompleted,
                treatment: hasCompleted === true ? toIntOrNull(values.treatment) : null,
                convenience: hasCompleted === true ? toIntOrNull(values.convenience) : null,
                facility: hasCompleted === true ? toIntOrNull(values.facility) : null,
                commendations: hasCompleted === true ? values.commendations : '',
                comments: hasCompleted === true ? values.comments : '',
            };
        },
        (nextStep) => ({ nextStep }),
    );

    return { model, isLoading, error, submit, isSubmitting, submitError };
};
